import { assign, fromPromise, setup } from 'xstate';
import { userService as userServiceImport } from '../../apiServices/UserService';
import { UserData } from './useUser';

export type AuthenticationContext = {
  magicToken?: string;
  isAuthenticated: boolean;
  userData?: UserData;
};

export type AuthenticationMachine = {
  userService: typeof userServiceImport;
  initialContext?: Partial<AuthenticationContext>;
  initialState?: string;
};

export const machine = ({
  userService,
  initialContext,
  initialState = 'INITIAL',
}: AuthenticationMachine) =>
  setup({
    types: {
      input: {} as {
        magicToken?: string;
      },
      context: {} as AuthenticationContext,
      events: {} as
        | { type: 'SIGN_UP' }
        | { type: 'SIGN_OUT' }
        | { type: 'POLL_IS_AUTHENTICATED' },
    },
    actions: {
      setIsSignedIn: assign(() => ({
        isAuthenticated: true,
      })),
      setIsUnauthenticated: assign(() => ({
        isAuthenticated: false,
        userData: undefined,
      })),
      setUserData: assign((_context, params: { userData: UserData }) => ({
        userData: params.userData,
        isAuthenticated: true,
      })),
    },
    actors: {
      fetchVerify: fromPromise(({ input }: { input: { magicToken: string } }) =>
        userService.verifyAuth(input.magicToken)
      ),
      checkUserSignedIn: fromPromise(() =>
        userService.getUser().then((user) => user)
      ),
      signOutUser: fromPromise(() => userService.signOut()),
    },
    guards: {
      isSignedIn: ({ context }) => context.isAuthenticated,
      hasMagicToken: ({ context }) => context.magicToken !== undefined,
    },
  }).createMachine({
    context: ({ input }) => ({
      magicToken: input?.magicToken ?? undefined,
      isAuthenticated: false,
      ...initialContext,
    }),
    id: 'AuthenticationMachine',
    initial: initialState,
    states: {
      INITIAL: {
        always: [
          {
            target: 'VERIFY_MAGIC_TOKEN',
            guard: {
              type: 'hasMagicToken',
            },
          },
          {
            target: 'CHECK_IF_AUTHENTICATED',
          },
        ],
      },
      VERIFY_MAGIC_TOKEN: {
        invoke: {
          src: 'fetchVerify',
          input: ({ context }) => ({
            magicToken: context.magicToken!,
          }),
          onDone: {
            target: 'CHECK_IF_AUTHENTICATED',
            actions: {
              type: 'setIsSignedIn',
            },
          },
          onError: {
            target: 'UI_SIGN_IN',
          },
        },
      },
      CHECK_IF_AUTHENTICATED: {
        on: {
          SIGN_OUT: {
            target: 'SIGN_OUT_USER',
          },
          POLL_IS_AUTHENTICATED: {
            target: 'CHECK_IF_AUTHENTICATED',
            reenter: true,
          },
        },
        invoke: {
          src: 'checkUserSignedIn',
          input: {},
          onDone: {
            actions: {
              type: 'setUserData',
              params: ({ event }) => ({
                userData: event.output,
              }),
            },
          },
          onError: {
            target: '#AuthenticationMachine.UI_SIGN_IN',
          },
        },
      },
      SIGN_OUT_USER: {
        invoke: {
          src: 'signOutUser',
          input: () => ({}),
          onDone: {
            target: 'UI_SIGN_IN',
            actions: {
              type: 'setIsUnauthenticated',
            },
          },
          onError: {
            target: 'UI_SIGN_IN',
            actions: {
              type: 'setIsUnauthenticated',
            },
          },
        },
      },
      UI_SIGN_IN: {
        on: {
          SIGN_UP: {
            target: 'CHECK_IF_AUTHENTICATED',
            actions: {
              type: 'setIsSignedIn',
            },
          },
        },
      },
    },
  });
